// Employee bank & personal details — 13 optional fields (plus the existing
// `date_of_birth`) that ride on the existing employee create / update / view
// endpoints. Mirrors the backend's `profileFieldsSchema` (employeeRoutes.js).
// Shared by the Society Portal pages and the Society Controller modal.

// Enum values are case-sensitive on the server — "married" is rejected.
export const MARITAL_STATUS_OPTIONS = [
    { value: 'SINGLE', label: 'Single' },
    { value: 'MARRIED', label: 'Married' },
    { value: 'DIVORCED', label: 'Divorced' },
    { value: 'WIDOWED', label: 'Widowed' },
];

export const BLOOD_GROUP_OPTIONS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']
    .map((v) => ({ value: v, label: v }));

export const BANK_ACCOUNT_TYPE_OPTIONS = [
    { value: 'SAVINGS', label: 'Savings' },
    { value: 'CURRENT', label: 'Current' },
    { value: 'SALARY', label: 'Salary' },
];

export const PAN_REGEX = /^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/;
export const AADHAR_REGEX = /^\d{12}$/;
export const BANK_ACCOUNT_REGEX = /^\d{6,30}$/;
export const IFSC_REGEX = /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/;

// Field → max length (free-text fields only; formatted/enum fields are
// covered by their regex or option list).
const MAX_LENGTHS = {
    nationality: 50,
    qualification: 100,
    bank_account_holder_name: 100,
    bank_name: 100,
    bank_branch_name: 100,
    upi_id: 100,
};

// `date_of_birth` is an existing field, not one of the 13 new ones — it lives
// here so the personal section is one list. Unlike the new fields it rejects
// "", which is fine: buildProfilePayload sends null for an emptied field.
export const PROFILE_FIELD_KEYS = [
    'date_of_birth', 'marital_status', 'blood_group',
    'nationality', 'qualification', 'pan_number', 'aadhar_number',
    'bank_account_holder_name', 'bank_name', 'bank_account_number', 'bank_ifsc_code',
    'bank_branch_name', 'bank_account_type', 'upi_id',
];

// Client-only — the server has no "confirm account number" check. Never sent.
export const CONFIRM_ACCOUNT_KEY = 'bank_account_number_confirm';

export const emptyProfile = {
    ...Object.fromEntries(PROFILE_FIELD_KEYS.map((k) => [k, ''])),
    [CONFIRM_ACCOUNT_KEY]: '',
};

// Form state from an employee record. Pre-fills the confirm box with the stored
// account number so an untouched account doesn't have to be re-typed on edit.
export const profileFromEmployee = (emp) => {
    const out = { ...emptyProfile };
    PROFILE_FIELD_KEYS.forEach((k) => { out[k] = emp?.[k] ?? ''; });
    // <input type=date> needs exactly YYYY-MM-DD.
    out.date_of_birth = String(out.date_of_birth).slice(0, 10);
    out[CONFIRM_ACCOUNT_KEY] = out.bank_account_number;
    return out;
};

// Normalise as the user types: digits only for Aadhaar / account numbers
// (the server rejects spaces and dashes), upper-case for PAN / IFSC (the
// server upper-cases them anyway — doing it here avoids a surprise on reload).
export const normalizeProfileInput = (name, value) => {
    switch (name) {
        case 'aadhar_number':
        case 'bank_account_number':
        case CONFIRM_ACCOUNT_KEY:
            return String(value).replace(/\D/g, '');
        case 'pan_number':
        case 'bank_ifsc_code':
            return String(value).replace(/\s/g, '').toUpperCase();
        default:
            return value;
    }
};

const str = (v) => (v == null ? '' : String(v).trim());

export const validateProfile = (values) => {
    const e = {};
    Object.entries(MAX_LENGTHS).forEach(([k, max]) => {
        if (str(values[k]).length > max) e[k] = `Max ${max} characters`;
    });
    const pan = str(values.pan_number);
    if (pan && !PAN_REGEX.test(pan)) e.pan_number = 'Invalid PAN (e.g. ABCDE1234F)';
    const aadhar = str(values.aadhar_number);
    if (aadhar && !AADHAR_REGEX.test(aadhar)) e.aadhar_number = 'Aadhaar must be 12 digits';
    const account = str(values.bank_account_number);
    if (account && !BANK_ACCOUNT_REGEX.test(account)) e.bank_account_number = 'Account number must be 6–30 digits';
    if (account && str(values[CONFIRM_ACCOUNT_KEY]) !== account) e[CONFIRM_ACCOUNT_KEY] = 'Account numbers do not match';
    const ifsc = str(values.bank_ifsc_code);
    if (ifsc && !IFSC_REGEX.test(ifsc)) e.bank_ifsc_code = 'Invalid IFSC (e.g. HDFC0001234)';
    return e;
};

/**
 * Request-body slice for the profile fields.
 * - add:  empty fields are omitted.
 * - edit: empty fields are sent as `null` — the update is partial, so omitting
 *         a key would keep the stored value instead of clearing it.
 */
export const buildProfilePayload = (values, { isEdit }) => {
    const out = {};
    PROFILE_FIELD_KEYS.forEach((k) => {
        const v = str(values[k]);
        if (v) out[k] = v;
        else if (isEdit) out[k] = null;
    });
    return out;
};

// Split a flat form into { profile, rest } so profile keys (and the client-only
// confirm key) never leak into the base payload — unknown keys are a 400.
export const splitProfile = (form) => {
    const profile = {};
    const rest = {};
    Object.entries(form).forEach(([k, v]) => {
        if (PROFILE_FIELD_KEYS.includes(k) || k === CONFIRM_ACCOUNT_KEY) profile[k] = v;
        else rest[k] = v;
    });
    return { profile, rest };
};

// "XXXXXX1234" — bank account, PAN and Aadhaar come back unmasked to anyone
// with employee.read, so read-only screens mask them.
export const maskValue = (v, visible = 4) => {
    const s = str(v);
    if (!s) return '';
    if (s.length <= visible) return s;
    return 'X'.repeat(s.length - visible) + s.slice(-visible);
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "1990-05-14" → "14 May 1990". Parsed by hand: `new Date()` treats a bare
// date as UTC midnight, which can render as the previous day in some zones.
export const formatPlainDate = (v) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str(v));
    if (!m) return str(v);
    return `${m[3]} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
};

export const optionLabel = (options, value) =>
    options.find((o) => o.value === value)?.label || value || '';
